import { DollarSign, Percent } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
	Description,
	ErrorMessage,
	Field,
	FieldGroup,
	Label,
} from "@/components/catalyst/Fieldset";
import { Input, InputGroup } from "@/components/catalyst/Input";
import { Switch, SwitchField } from "@/components/catalyst/Switch";
import type { CreateReferralLinkFormValues } from "../../schema";

interface FinancesSectionProps {
	/** When true, financial fields (payout rate, duration, expiry) are disabled */
	disabled?: boolean;
}

const collapseTransition = { duration: 0.2, ease: "easeInOut" as const };

export function FinancesSection({ disabled = false }: FinancesSectionProps) {
	const { setValue } = useFormContext<CreateReferralLinkFormValues>();

	const payoutRateBps = useWatch({ name: "payoutRateBps" });
	const maxRedemptions = useWatch({ name: "maxRedemptions" });
	const expiresAt = useWatch({ name: "expiresAt" });

	// Use local state to drive the toggles for immediate feedback and robust logic
	const [isTrackingOnly, setIsTrackingOnly] = useState(
		Number(payoutRateBps) === 0 && disabled,
	);
	const [isUnlimited, setIsUnlimited] = useState(maxRedemptions === null);
	const [isNoExpiry, setIsNoExpiry] = useState(expiresAt === null);

	// Sync local state when form values change externally (e.g. data load or reset)
	// We only sync if the deviation is meaningful to avoid fighting user input
	useEffect(() => {
		if (maxRedemptions === null && !isUnlimited) {
			setIsUnlimited(true);
		} else if (maxRedemptions !== null && isUnlimited) {
			// This case is tricky: if user just toggled ON (making it null), we don't want to sync back.
			// Ideally, we trust the form value.
			// But if we toggle OFF, we set it to 100.
			// So if it IS a number, isUnlimited should be false.
			setIsUnlimited(false);
		}
	}, [maxRedemptions, isUnlimited]);

	useEffect(() => {
		if (expiresAt === null && !isNoExpiry) {
			setIsNoExpiry(true);
		} else if (expiresAt !== null && isNoExpiry) {
			setIsNoExpiry(false);
		}
	}, [expiresAt, isNoExpiry]);

	return (
		<FieldGroup className="space-y-4!">
			<div className="rounded-lg border border-base-content/10 p-4">
				<SwitchField disabled={disabled}>
					<Label>Tracking only — no partner payouts</Label>
					<Switch
						color="primary"
						checked={isTrackingOnly}
						disabled={disabled}
						onChange={(checked) => {
							setIsTrackingOnly(checked);
							if (checked) {
								setValue("payoutRateBps", 0, {
									shouldValidate: true,
									shouldDirty: true,
								});
								setValue("payoutDurationDays", null, {
									shouldValidate: true,
									shouldDirty: true,
								});
							} else {
								setValue("payoutRateBps", "" as unknown as number, {
									shouldValidate: true,
									shouldDirty: true,
								});
							}
						}}
					/>
				</SwitchField>
				{disabled && (
					<p className="mt-4 text-sm text-base-content/60">
						To change, delete this link and create a new one
					</p>
				)}
				<AnimatePresence initial={false}>
					{!isTrackingOnly && (
						<motion.div
							initial={{ height: 0, opacity: 0, overflow: "hidden" }}
							animate={{ height: "auto", opacity: 1, overflow: "visible" }}
							exit={{ height: 0, opacity: 0, overflow: "hidden" }}
							transition={collapseTransition}
						>
							<div className="space-y-8 pt-4">
								<Controller
									name="payoutRateBps"
									render={({
										field: { onChange, onBlur, value, ref },
										fieldState: { error },
									}) => (
										<Field disabled={disabled}>
											<Label>Payout Rate (Percent)</Label>
											<InputGroup>
												<Input
													type="number"
													value={value ?? ""}
													onChange={onChange}
													onBlur={onBlur}
													ref={ref}
													placeholder="e.g. 5"
													disabled={disabled}
												/>
												<Percent
													data-slot="icon"
													className="size-5 text-base-content/50"
												/>
											</InputGroup>
											{disabled && (
												<Description>
													To change, delete this link and create a new one
												</Description>
											)}
											{error && <ErrorMessage>{error.message}</ErrorMessage>}
										</Field>
									)}
								/>

								<Controller
									name="payoutDurationDays"
									render={({
										field: { onChange, onBlur, value, ref },
										fieldState: { error },
									}) => (
										<Field disabled={disabled}>
											<Label>Duration (Days)</Label>
											<Input
												type="number"
												value={value ?? ""}
												onChange={onChange}
												onBlur={onBlur}
												ref={ref}
												placeholder="Optional"
												disabled={disabled}
											/>
											<Description>
												{disabled
													? "To change, delete this link and create a new one"
													: "Empty for indefinite"}
											</Description>
											{error && <ErrorMessage>{error.message}</ErrorMessage>}
										</Field>
									)}
								/>

								<Controller
									name="minPayoutDollars"
									render={({
										field: { onChange, onBlur, value, ref },
										fieldState: { error },
									}) => (
										<Field disabled={disabled}>
											<Label>Minimum Payout ($)</Label>
											<InputGroup>
												<DollarSign
													data-slot="icon"
													className="size-5 text-base-content/50"
												/>
												<Input
													type="number"
													value={value ?? ""}
													onChange={onChange}
													onBlur={onBlur}
													ref={ref}
													placeholder="e.g. 25"
													disabled={disabled}
												/>
											</InputGroup>
											<Description>
												{disabled
													? "To change, delete this link and create a new one"
													: "Partner earnings must reach this amount before a payout is issued"}
											</Description>
											{error && <ErrorMessage>{error.message}</ErrorMessage>}
										</Field>
									)}
								/>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</div>

			<div className="rounded-lg border border-base-content/10 p-4">
				<SwitchField disabled={disabled}>
					<Label>Allow unlimited redemptions</Label>
					<Switch
						color="primary"
						checked={isUnlimited}
						disabled={disabled}
						onChange={(checked) => {
							setIsUnlimited(checked);
							if (checked) {
								setValue("maxRedemptions", null, {
									shouldValidate: true,
									shouldDirty: true,
								});
							} else {
								setValue("maxRedemptions", 100, {
									shouldValidate: true,
									shouldDirty: true,
								});
							}
						}}
					/>
				</SwitchField>
				{disabled && (
					<p className="mt-4 text-sm text-base-content/60">
						To change, delete this link and create a new one
					</p>
				)}
				<AnimatePresence initial={false}>
					{!isUnlimited && (
						<motion.div
							initial={{ height: 0, opacity: 0, overflow: "hidden" }}
							animate={{ height: "auto", opacity: 1, overflow: "visible" }}
							exit={{ height: 0, opacity: 0, overflow: "hidden" }}
							transition={collapseTransition}
						>
							<div className="pt-4">
								<Controller
									name="maxRedemptions"
									render={({
										field: { onChange, onBlur, value, ref },
										fieldState: { error },
									}) => (
										<Field disabled={disabled}>
											<Label>Max Redemptions</Label>
											<Input
												type="number"
												value={value ?? ""}
												onChange={(e) => {
													const v = e.target.value;
													onChange(v === "" ? null : Number(v));
												}}
												onBlur={onBlur}
												ref={ref}
												placeholder="e.g. 100"
												disabled={disabled}
											/>
											{error && <ErrorMessage>{error.message}</ErrorMessage>}
										</Field>
									)}
								/>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</div>

			<div className="rounded-lg border border-base-content/10 p-4">
				<SwitchField disabled={disabled}>
					<Label>Never expire</Label>
					<Switch
						color="primary"
						checked={isNoExpiry}
						disabled={disabled}
						onChange={(checked) => {
							setIsNoExpiry(checked);
							if (checked) {
								setValue("expiresAt", null, {
									shouldValidate: true,
									shouldDirty: true,
								});
							} else {
								const tomorrow = new Date();
								tomorrow.setDate(tomorrow.getDate() + 1);
								setValue("expiresAt", tomorrow.toISOString(), {
									shouldValidate: true,
									shouldDirty: true,
								});
							}
						}}
					/>
				</SwitchField>
				{disabled && (
					<p className="mt-4 text-sm text-base-content/60">
						To change, delete this link and create a new one
					</p>
				)}
				<AnimatePresence initial={false}>
					{!isNoExpiry && (
						<motion.div
							initial={{ height: 0, opacity: 0, overflow: "hidden" }}
							animate={{ height: "auto", opacity: 1, overflow: "visible" }}
							exit={{ height: 0, opacity: 0, overflow: "hidden" }}
							transition={collapseTransition}
						>
							<div className="pt-4">
								<Field disabled={disabled}>
									<Label>Expiry Date</Label>
									<Controller
										name="expiresAt"
										render={({
											field: { onChange, value, onBlur, ref },
											fieldState: { error },
										}) => {
											const dateValue = value ? value.split("T")[0] : "";
											const timeValue = value?.includes("T")
												? value.split("T")[1]
												: "";

											return (
												<>
													<div className="mt-3 flex gap-4">
														<div className="flex-1">
															<Label className="mb-1 block text-base-content/50">
																Date
															</Label>
															<Input
																type="date"
																value={dateValue}
																ref={ref}
																onBlur={onBlur}
																disabled={disabled}
																onChange={(e) => {
																	const newDate = e.target.value;
																	if (!newDate) {
																		onChange(null);
																		return;
																	}
																	onChange(
																		`${newDate}T${timeValue || "00:00:00"}`,
																	);
																}}
															/>
														</div>
														<div className="w-32">
															<Label className="mb-1 block text-base-content/50">
																Time
															</Label>
															<Input
																type="time"
																value={timeValue}
																disabled={disabled}
																onChange={(e) => {
																	const newTime = e.target.value;
																	if (dateValue) {
																		onChange(`${dateValue}T${newTime}`);
																	}
																}}
															/>
														</div>
													</div>
													{error && (
														<ErrorMessage>{error.message}</ErrorMessage>
													)}
												</>
											);
										}}
									/>
								</Field>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</FieldGroup>
	);
}
